import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  ArrowRight,
  CreditCard,
  Download,
  Lock,
  Package,
  Truck,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const VARIANTS = {
  butterfly_effect_digital: {
    id: "butterfly_effect_digital",
    label: "Digital",
    price: 49.99,
    physical: false,
    icon: Download,
    sub: "Instant download · PDF + EPUB",
  },
  butterfly_effect_physical: {
    id: "butterfly_effect_physical",
    label: "Signed Hardcover",
    price: 99.99,
    physical: true,
    icon: Package,
    sub: "Signed + free worldwide shipping",
  },
};

const COUNTRIES = [
  { code: "US", label: "United States" },
  { code: "CA", label: "Canada" },
  { code: "GB", label: "United Kingdom" },
  { code: "AU", label: "Australia" },
  { code: "OTHER", label: "Other" },
];

const ELEMENTS_APPEARANCE = {
  theme: "night",
  variables: {
    colorPrimary: "#DC2626",
    colorBackground: "rgba(255,255,255,0.05)",
    colorText: "#ffffff",
    colorDanger: "#ff5252",
    fontFamily: "ui-sans-serif, system-ui, sans-serif",
    spacingUnit: "4px",
    borderRadius: "12px",
  },
  rules: {
    ".Input": {
      border: "1px solid rgba(255,255,255,0.18)",
      backgroundColor: "rgba(255,255,255,0.06)",
      color: "#ffffff",
      padding: "12px 14px",
    },
    ".Input:focus": {
      border: "1px solid #DC2626",
      boxShadow: "0 0 0 2px rgba(220,38,38,0.35)",
    },
    ".Label": {
      color: "rgba(255,255,255,0.65)",
      fontSize: "11px",
      textTransform: "uppercase",
      letterSpacing: "0.18em",
      fontFamily: "ui-monospace, SFMono-Regular, monospace",
      marginBottom: "6px",
    },
    ".Tab": {
      border: "1px solid rgba(255,255,255,0.18)",
      backgroundColor: "rgba(255,255,255,0.04)",
      color: "rgba(255,255,255,0.75)",
    },
    ".Tab--selected": {
      border: "1px solid rgba(220,38,38,0.55)",
      backgroundColor: "rgba(220,38,38,0.10)",
      color: "#ffffff",
    },
  },
};

/** Inner form (must be wrapped in <Elements>) */
function CheckoutForm({
  variant,
  name,
  email,
  shipping,
  paymentIntentId,
  onSubmittingChange,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    if (!name.trim() || !email.trim()) {
      toast.error("Add your name and email so Cameron can deliver.");
      return;
    }
    if (variant.physical) {
      if (!shipping.line1 || !shipping.city || !shipping.postal_code) {
        toast.error("Shipping address is incomplete.");
        return;
      }
    }
    setSubmitting(true);
    onSubmittingChange?.(true);
    setErrorMsg("");

    const returnUrl = `${window.location.origin}/book/success?payment_intent=${paymentIntentId}`;
    const billingDetails = {
      name: name.trim(),
      email: email.trim(),
    };
    if (variant.physical) {
      billingDetails.address = {
        line1: shipping.line1,
        line2: shipping.line2 || "",
        city: shipping.city,
        state: shipping.state || "",
        postal_code: shipping.postal_code,
        country: shipping.country || "US",
      };
    }

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
        payment_method_data: { billing_details: billingDetails },
      },
    });

    if (error) {
      setErrorMsg(
        error.message || "Payment couldn't be completed. Please try again."
      );
      toast.error(error.message || "Payment failed");
      setSubmitting(false);
      onSubmittingChange?.(false);
    }
    // Otherwise stripe redirects to return_url
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 flex items-center gap-2">
            <CreditCard className="h-3.5 w-3.5" /> Payment
          </span>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">
            <Lock className="h-3 w-3 text-[#FCA5A5]" /> Stripe-secured
          </span>
        </div>
        <PaymentElement
          options={{ layout: "tabs" }}
          data-testid="stripe-payment-element"
        />
      </div>

      {errorMsg ? (
        <div
          role="alert"
          data-testid="payment-error"
          className="text-sm text-[#ff8a8a] bg-[#5a1f1f]/40 border border-[#ff5252]/30 rounded-xl px-4 py-3"
        >
          {errorMsg}
        </div>
      ) : null}

      <button
        type="submit"
        disabled={!stripe || submitting}
        data-testid="buy-pay"
        className="cta-clay-blue group inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full text-xs md:text-sm font-mono font-semibold uppercase tracking-[0.2em] text-white disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <span className="relative z-10">
          {submitting ? "Processing…" : `Pay $${variant.price.toFixed(2)}`}
        </span>
        <ArrowRight className="relative z-10 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </button>

      <span className="text-[11px] text-white/45 leading-relaxed">
        We never see or store your card information. Card is charged only when
        you press Pay.
      </span>
    </form>
  );
}

export default function BuyModal({ open, onOpenChange, initialVariantId = null }) {
  const variant = useMemo(
    () => VARIANTS[initialVariantId] || VARIANTS.butterfly_effect_physical,
    [initialVariantId]
  );

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [city, setCity] = useState("");
  const [stateRegion, setStateRegion] = useState("");
  const [postal, setPostal] = useState("");
  const [country, setCountry] = useState("US");

  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [intentError, setIntentError] = useState("");
  const [intentLoading, setIntentLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setClientSecret("");
      setPaymentIntentId("");
      setIntentError("");
      setIntentLoading(false);
      setSubmitting(false);
    }
  }, [open]);

  const detailsValid = useMemo(() => {
    if (!name.trim()) return false;
    const e = email.trim();
    // proper email shape: something@something.tld
    const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!EMAIL_RX.test(e)) return false;
    if (variant.physical) {
      if (!line1.trim() || !city.trim() || !postal.trim()) return false;
    }
    return true;
  }, [name, email, line1, city, postal, variant.physical]);

  // Build shipping payload (memoized) for stable deps
  const shippingPayload = useMemo(
    () =>
      variant.physical
        ? {
            line1: line1.trim(),
            line2: line2.trim(),
            city: city.trim(),
            state: stateRegion.trim(),
            postal_code: postal.trim(),
            country,
          }
        : null,
    [variant.physical, line1, line2, city, stateRegion, postal, country]
  );

  // Lazily create / refresh the PaymentIntent when details become valid
  useEffect(() => {
    if (!open) return;
    if (!detailsValid) {
      setClientSecret("");
      setPaymentIntentId("");
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setIntentLoading(true);
        setIntentError("");
        const payload = {
          product_id: variant.id,
          customer_email: email.trim(),
          customer_name: name.trim(),
        };
        if (variant.physical) payload.shipping = shippingPayload;
        const res = await axios.post(`${API}/api/payment-intent`, payload);
        if (cancelled) return;
        setClientSecret(res.data.client_secret);
        setPaymentIntentId(res.data.payment_intent_id);
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail || "Couldn't initialize payment.";
        // Suppress the "Invalid email" error since users see it as they type
        const msg = typeof detail === "string" ? detail : "Couldn't initialize payment.";
        setIntentError(msg.toLowerCase().includes("email") ? "" : msg);
      } finally {
        if (!cancelled) setIntentLoading(false);
      }
    }, 350); // debounce
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, detailsValid, variant.id, variant.physical, name, email, shippingPayload]);

  const Icon = variant.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="buy-modal"
        className="max-w-xl w-[calc(100%-2rem)] max-h-[92vh] overflow-y-auto p-0 bg-gradient-to-br from-[#0A0A0C] via-black to-[#070710] border border-white/12 text-white rounded-2xl md:rounded-3xl shadow-[0_30px_120px_rgba(220,38,38,0.18)]"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 w-[26rem] h-[26rem] opacity-50"
          style={{
            background:
              "radial-gradient(closest-side, rgba(220,38,38,0.30) 0%, transparent 70%)",
            filter: "blur(40px)",
          }}
        />

        <div className="relative p-7 md:p-9">
          <DialogHeader className="space-y-3 mb-6">
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55">
              The Butterfly Effect · Checkout
            </span>
            <DialogTitle className="font-display font-black tracking-tighter leading-[0.95] text-3xl md:text-4xl text-white text-left">
              Almost{" "}
              <span
                className="font-script italic font-normal whitespace-nowrap"
                style={{
                  background:
                    "linear-gradient(180deg, #ffffff 0%, #FCA5A5 30%, #DC2626 70%, #7F1D1D 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent",
                  paddingBottom: "0.18em",
                  paddingRight: "0.05em",
                  display: "inline-block",
                  lineHeight: 1,
                }}
              >
                yours.
              </span>
            </DialogTitle>
            <DialogDescription className="text-sm text-white/60 leading-relaxed text-left">
              {variant.physical
                ? "Tell us where to ship it, and pay in one step."
                : "We'll send the download link right after payment."}
            </DialogDescription>
          </DialogHeader>

          {/* Selected variant summary */}
          <div
            className="flex items-center gap-4 p-4 rounded-2xl border border-[#DC2626]/40 bg-gradient-to-br from-[#1A0608] via-[#0F0405] to-black mb-5"
            data-testid="buy-selected-summary"
          >
            <div className="inline-flex items-center justify-center h-11 w-11 shrink-0 rounded-xl border border-white/10 bg-white/[0.04]">
              <Icon className="h-5 w-5 text-[#FCA5A5]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm md:text-base text-white font-medium leading-tight">
                {variant.label}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-white/45 mt-1">
                {variant.sub}
              </div>
            </div>
            <span
              className="font-display font-black tracking-tighter text-3xl md:text-4xl"
              style={{
                background:
                  "linear-gradient(180deg, #ffffff 0%, #FCA5A5 60%, #DC2626 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                color: "transparent",
              }}
            >
              ${variant.price.toFixed(2)}
            </span>
          </div>

          {/* Buyer + shipping fields */}
          <div className="flex flex-col gap-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="buy-modal-name"
                className="h-12 rounded-full border-white/20 bg-white/10 text-white placeholder:text-white/45 focus-visible:ring-2 focus-visible:ring-[#DC2626] focus-visible:ring-offset-0 focus-visible:border-[#DC2626]/60 px-5"
              />
              <Input
                type="email"
                placeholder="Email for receipt"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="buy-modal-email"
                className="h-12 rounded-full border-white/20 bg-white/10 text-white placeholder:text-white/45 focus-visible:ring-2 focus-visible:ring-[#DC2626] focus-visible:ring-offset-0 focus-visible:border-[#DC2626]/60 px-5"
              />
            </div>

            {variant.physical ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 flex items-center gap-2">
                    <Truck className="h-3.5 w-3.5" /> Shipping address
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#FCA5A5]">
                    Free worldwide
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    placeholder="Address line 1"
                    value={line1}
                    onChange={(e) => setLine1(e.target.value)}
                    required
                    data-testid="buy-modal-line1"
                    className="h-12 rounded-full border-white/20 bg-white/10 text-white placeholder:text-white/45 focus-visible:ring-2 focus-visible:ring-[#DC2626] focus-visible:ring-offset-0 focus-visible:border-[#DC2626]/60 px-5 md:col-span-2"
                  />
                  <Input
                    placeholder="Apt, suite, etc. (optional)"
                    value={line2}
                    onChange={(e) => setLine2(e.target.value)}
                    data-testid="buy-modal-line2"
                    className="h-12 rounded-full border-white/20 bg-white/10 text-white placeholder:text-white/45 focus-visible:ring-2 focus-visible:ring-[#DC2626] focus-visible:ring-offset-0 focus-visible:border-[#DC2626]/60 px-5 md:col-span-2"
                  />
                  <Input
                    placeholder="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    required
                    data-testid="buy-modal-city"
                    className="h-12 rounded-full border-white/20 bg-white/10 text-white placeholder:text-white/45 focus-visible:ring-2 focus-visible:ring-[#DC2626] focus-visible:ring-offset-0 focus-visible:border-[#DC2626]/60 px-5"
                  />
                  <Input
                    placeholder="State / Region"
                    value={stateRegion}
                    onChange={(e) => setStateRegion(e.target.value)}
                    data-testid="buy-modal-state"
                    className="h-12 rounded-full border-white/20 bg-white/10 text-white placeholder:text-white/45 focus-visible:ring-2 focus-visible:ring-[#DC2626] focus-visible:ring-offset-0 focus-visible:border-[#DC2626]/60 px-5"
                  />
                  <Input
                    placeholder="Postal / Zip"
                    value={postal}
                    onChange={(e) => setPostal(e.target.value)}
                    required
                    data-testid="buy-modal-postal"
                    className="h-12 rounded-full border-white/20 bg-white/10 text-white placeholder:text-white/45 focus-visible:ring-2 focus-visible:ring-[#DC2626] focus-visible:ring-offset-0 focus-visible:border-[#DC2626]/60 px-5"
                  />
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    data-testid="buy-modal-country"
                    className="h-12 rounded-full border border-white/20 bg-white/10 text-white px-5 focus-visible:ring-2 focus-visible:ring-[#DC2626] focus-visible:outline-none appearance-none"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code} className="bg-black text-white">
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : null}

            {/* Payment Element area */}
            {!detailsValid ? (
              <div
                className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-5 text-center"
                data-testid="payment-locked"
              >
                <CreditCard className="h-5 w-5 text-white/35 mx-auto mb-3" />
                <p className="text-sm text-white/55">
                  Fill in {variant.physical ? "your details and shipping address" : "your name and email"}{" "}
                  to enter payment info.
                </p>
              </div>
            ) : intentLoading && !clientSecret ? (
              <div
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-center text-sm text-white/55"
                data-testid="payment-loading"
              >
                Preparing secure payment…
              </div>
            ) : intentError ? (
              <div
                className="rounded-2xl border border-[#ff5252]/30 bg-[#5a1f1f]/30 p-5 text-sm text-[#ff8a8a]"
                data-testid="payment-intent-error"
              >
                {intentError}
              </div>
            ) : clientSecret && stripePromise ? (
              <Elements
                key={clientSecret}
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: ELEMENTS_APPEARANCE,
                }}
              >
                <CheckoutForm
                  variant={variant}
                  name={name}
                  email={email}
                  shipping={shippingPayload}
                  paymentIntentId={paymentIntentId}
                  onSubmittingChange={setSubmitting}
                />
              </Elements>
            ) : null}

            {!stripePromise ? (
              <p className="text-xs text-red-300/80">
                Stripe publishable key not configured.
              </p>
            ) : null}

            {submitting ? (
              <p className="text-[11px] text-white/45 text-center">
                Don't close this window while we process your payment…
              </p>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
